import {
  Button, ButtonDay, Container, ContainerButtons, ContainerExceptionGrid, ContainerHoursView,
} from './styles';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Edit5Icon } from '~/icons';
import { colors } from '~/styles/colors';
import { apiCall } from '~/providers';
import { TExceptions, TListHour } from './edit';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { NewPagination } from '~/components/NewPagination';

const dayLabels: Record<keyof TListHour['selectedDays'], string> = {
  sun: t('diasDaSemana.dom'),
  mon: t('diasDaSemana.seg'),
  tue: t('diasDaSemana.ter'),
  wed: t('diasDaSemana.qua'),
  thu: t('diasDaSemana.qui'),
  fri: t('diasDaSemana.sex'),
  sat: t('diasDaSemana.sab'),
};

const ITEMS_PER_PAGE = 5;

export function OpeningHoursProfile({ haveAcessEdit = false }: Readonly<{ haveAcessEdit: boolean }>) {
  const [groupedByDay] = useState<Record<string, { open: string; close: string }[]>>({});
  const [operatingHours, setOperatingHours] = useState<TListHour[]>();
  const [exceptionHours, setExceptionHours] = useState<TExceptions[]>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sheet, setSheet] = useState<'hours' | 'exceptions'>('hours');
  const match = useRouteMatch<{ unitId: string }>();
  const history = useHistory();
  const totalPages = Math.ceil((exceptionHours || []).length / ITEMS_PER_PAGE);
  const handleChangePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  function decideData() {
    return (exceptionHours || []).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }
  const dataPerPage = useMemo(() => decideData(), [exceptionHours, currentPage]);

  function redirectToEdit() {
    history.push(`/analise/unidades/perfil/${match.params.unitId}/editar-unidade`);
    setTimeout(() => {
      window.scrollTo({
        top: 800,
        behavior: 'smooth',
      });
    }, 100);
  }

  useEffect(() => {
    setCurrentPage(currentPage ?? 1);
  }, [exceptionHours]);

  function returnButton() {
    if (operatingHours?.length) {
      return (
        <Button onClick={() => redirectToEdit()}>
          <Edit5Icon size="14" color={colors.icon['primary-base']} />
          <span>{t('editarHorarioFuncionamento')}</span>
        </Button>
      );
    }
    return (
      <Button onClick={() => redirectToEdit()}>
        <ClockCircleOutlined onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
        <span>{t('adicionarHorarioFuncionamento')}</span>
      </Button>
    );
  }

  async function getOperatingHoursUnit() {
    try {
      const { listHours, listExceptions } = await apiCall('/unit/get-operating-hours', { unitId: Number(match.params.unitId) });
      setOperatingHours(listHours);
      setExceptionHours(listExceptions);
      Object.values(dayLabels).forEach((dayName) => {
        groupedByDay[dayName] = [];
      });

      listHours.forEach((item) => {
        Object.entries(item.selectedDays).forEach(([dayKey, selected]) => {
          const dayName = dayLabels[dayKey as keyof typeof dayLabels];
          if (selected) {
            groupedByDay[dayName].push({ open: item.openHour, close: item.closeHour });
          }
        });
      });
      Object.keys(groupedByDay).forEach((day) => {
        if (groupedByDay[day].length === 0) {
          groupedByDay[day].push({ open: '', close: '' });
        }
      });
    } catch (err) {
      toast.error(t('erroAoBuscarHorariosUnidade'));
      console.log(err);
    }
  }
  useEffect(() => {
    getOperatingHoursUnit();
  }, []);

  function sheetFunction(type: 'hours' | 'exceptions') {
    setSheet(type);
  }

  return (
    <Container>
      <h1>{t('horarioDeFuncionamento')}</h1>
      <ContainerButtons style={{ marginBottom: 10 }}>
        <ButtonDay selected={sheet === 'hours'} onClick={() => sheetFunction('hours')} type="button">{t('horarios')}</ButtonDay>
        <ButtonDay selected={sheet === 'exceptions'} onClick={() => sheetFunction('exceptions')} type="button">{t('excecoes')}</ButtonDay>
      </ContainerButtons>
      {
        sheet === 'exceptions' && (
          <div style={{ marginBottom: '10px', minHeight: exceptionHours ? 250 : 0, marginTop: 10 }}>
            {(!exceptionHours || (exceptionHours && exceptionHours.length === 0)) ? (
              <span style={{ margin: 0 }}>{t('nenhumaExcecaoHorarioCadastrado')}</span>
            ) : (
              <>
                <ContainerExceptionGrid>
                  <strong>{t('nome')}</strong>
                  <strong>{t('dia')}</strong>
                  <strong>{t('horarioAbertura')}</strong>
                  <strong>{t('horarioFechamento')}</strong>
                  <strong>{t('repeteAnualmente')}</strong>
                </ContainerExceptionGrid>
                {
                  dataPerPage?.map((item) => (
                    <ContainerExceptionGrid>
                      <span>{item.name.length > 20 ? `${item.name.slice(0, 20)}...` : item.name}</span>
                      <span>{item.repeat_every_year ? moment(item.day).format('DD/MM') : moment(item.day).format('DD/MM/YYYY')}</span>
                      <span>{`${item.openHour}hrs`}</span>
                      <span>{item.closeHour}</span>
                      <span>{item.repeat_every_year ? t('sim') : t('nao')}</span>
                    </ContainerExceptionGrid>
                  ))
                }
                {
                  exceptionHours?.length > 3 && (
                    <NewPagination
                      currentPage={currentPage}
                      onPageChange={handleChangePage}
                      totalPages={totalPages}
                    />
                  )
                }
              </>
            )}
          </div>
        )
      }
      {
        sheet === 'hours' && (
          <div style={{ display: 'flex', gap: '15px', margin: '10px 0px' }}>
            {operatingHours && operatingHours.length > 0 && Object.keys(groupedByDay).map((day) => {
              const intervals = groupedByDay[day];
              const sorted = [...intervals].sort((a, b) => a.open.localeCompare(b.open));
              return (
                <ContainerHoursView key={day}>
                  <div>
                    <strong>{day}</strong>
                  </div>
                  <div>
                    {sorted.map((interval, i) => (
                      <>
                        {hasMoreThanOneHour(sorted, i, interval)}
                      </>
                    ))}
                  </div>
                </ContainerHoursView>
              );
            })}
            {operatingHours && operatingHours.length === 0 && (
              <span>{t('nenhumHorarioCadastrado')}</span>
            )}
          </div>
        )
      }
      {haveAcessEdit && returnButton()}
    </Container>
  );
}

function hasMoreThanOneHour(days: {
    open: string;
    close: string;
}[], i: number, interval: { open: string; close: string }) {
  if (days.length > 1) {
    return (
      <div key={i}>
        <p>{i === 0 ? t('horarioAbertura') : t('horarioFechamento')}</p>
        <p>
          {interval.open}
          {' '}
          -
          {' '}
          {interval.close}
        </p>
      </div>
    );
  }
  return (
    <>
      <p>{t('horarioAbertura')}</p>
      <p>{interval.open || '-'}</p>
      <p>{t('horarioFechamento')}</p>
      <p>{interval.close || '-'}</p>
    </>
  );
}
